/**
 * 好友对战 — 创建房间页
 * 路由：/friend
 * 已登录用户可直接创建，生成链接分享给好友
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import API from '../api';
import { useApp } from '../store';
import { useLang, LangToggle } from '../i18n';

const GOLD = '#F5C518';

function friendHostKey(code) {
  return `friend_host_${String(code || '').toUpperCase()}`;
}

export default function FriendLobby() {
  const nav = useNavigate();
  const { user } = useApp();
  const [creating, setCreating] = useState(false);
  const [roomCode, setRoomCode] = useState('');
  const [copied, setCopied]     = useState(false);
  const [joinCode, setJoinCode] = useState('');
  const [error, setError]       = useState('');

  const { t, lang } = useLang();
  const hostName = user?.nickname || (lang==='en'?'Player':'玩家');

  async function createRoom() {
    setCreating(true);
    setError('');
    try {
      const r = await API.post('/api/friend-room/create', { hostName });
      setRoomCode(r.code);
      if (r.hostToken) localStorage.setItem(friendHostKey(r.code), r.hostToken);
    } catch (e) {
      setError(lang==='en'?'Failed to create room, please retry':'创建失败，请重试');
    } finally {
      setCreating(false);
    }
  }

  function copyLink() {
    const link = `${window.location.origin}/friend/${roomCode}`;
    navigator.clipboard?.writeText(link).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  function enterCreatedRoom() {
    nav(`/friend/${roomCode}`);
  }

  function joinRoom() {
    const code = joinCode.trim().toUpperCase();
    if (!code) { setError(lang==='en'?'Please enter a room code':'请输入房间码'); return; }
    nav(`/friend/${code}`);
  }

  const S = `
    *{box-sizing:border-box;margin:0;padding:0;-webkit-tap-highlight-color:transparent;}
    .fl-root{min-height:100vh;min-height:100dvh;
      background:linear-gradient(180deg,#0B1E4A 0%,#0D2B6B 50%,#091840 100%);
      padding-bottom:40px;}
    .fl-header{display:flex;align-items:center;gap:14px;padding:52px 20px 20px;}
    .fl-back{background:rgba(46,127,232,0.15);border:1px solid rgba(46,127,232,0.3);
      color:#8BA8D4;font-size:18px;cursor:pointer;padding:8px 12px;border-radius:10px;}
    .fl-title{font-size:20px;font-weight:800;color:#fff;}
    .fl-body{padding:0 16px;display:flex;flex-direction:column;gap:14px;}
    .fl-card{background:#0F2554;border-radius:18px;padding:20px;}
    .fl-label{font-size:11px;color:#4A6A9A;letter-spacing:1px;margin-bottom:10px;text-transform:uppercase;}
    .btn-gold{width:100%;padding:14px;border-radius:14px;border:none;cursor:pointer;
      font-size:15px;font-weight:900;
      background:linear-gradient(180deg,${GOLD} 0%,#E6A800 100%);color:#000;
      box-shadow:0 4px 0 #8B6200;}
    .btn-gold:active{transform:translateY(3px);box-shadow:0 1px 0 #8B6200;}
    .btn-gold:disabled{opacity:.4;cursor:not-allowed;transform:none;box-shadow:none;}
    .btn-blue{width:100%;padding:14px;border-radius:14px;border:none;cursor:pointer;
      font-size:15px;font-weight:900;
      background:linear-gradient(180deg,#3A8EF6 0%,#1A5FBF 100%);color:#fff;
      box-shadow:0 4px 0 #0F3A80;}
    .btn-blue:active{transform:translateY(3px);box-shadow:0 1px 0 #0F3A80;}
    .code-box{font-family:'Share Tech Mono',monospace;font-size:34px;font-weight:900;
      color:${GOLD};letter-spacing:8px;text-align:center;padding:16px 0 10px;}
    .link-text{font-size:12px;color:#4A6A9A;word-break:break-all;
      background:#0B1E4A;borderRadius:8px;padding:8px 12px;border-radius:8px;line-height:1.6;margin-bottom:12px;}
    .code-input{width:100%;padding:14px 16px;border-radius:12px;border:none;
      background:#0B1E4A;color:#fff;font-size:20px;font-weight:800;letter-spacing:4px;
      text-align:center;outline:none;text-transform:uppercase;}
    .code-input::placeholder{color:#4A6A9A;letter-spacing:2px;font-size:14px;font-weight:400;}
    .rule-row{display:flex;gap:10px;margin-bottom:10px;align-items:flex-start;}
    .rule-row:last-child{margin-bottom:0;}
    .rule-num{width:22px;height:22px;border-radius:50%;background:rgba(46,127,232,0.2);
      color:#3A8EF6;font-size:11px;font-weight:800;display:flex;align-items:center;
      justify-content:center;flex-shrink:0;margin-top:1px;}
    .rule-text{font-size:13px;color:#8BA8D4;line-height:1.6;}
    .rule-text strong{color:#fff;}
  `;

  return (
    <div className="fl-root">
      <style>{S}</style>
      <div className="fl-header">
        <button className="fl-back" onClick={() => nav('/lobby')}>&#8592;</button>
        <div className="fl-title">{t('friendBattleTitle')}</div>
        <LangToggle />
      </div>

      <div className="fl-body">
        {error && (
          <div style={{ padding: '12px 16px', borderRadius: 12, background: 'rgba(232,69,60,0.12)', color: '#E8453C', fontSize: 13, fontWeight: 600 }}>
            {error}
          </div>
        )}

        {/* 创建房间 */}
        <div className="fl-card">
          <div className="fl-label">{t('createRoom')}</div>
          {!roomCode ? (
            <>
              <div style={{ fontSize: 13, color: '#8BA8D4', lineHeight: 1.7, marginBottom: 14 }}>
                {lang==='en'?'Create a private battle room and share the link with friends.':'创建一个私人对战房间，把链接发给朋友，直接进来就能玩。'}
              </div>
              <button className="btn-gold" onClick={createRoom} disabled={creating}>
                {creating ? (lang==='en'?'Creating...':'创建中...') : (lang==='en'?'Create Room':'立即创建房间')}
              </button>
            </>
          ) : (
            <>
              <div className="code-box">{roomCode}</div>
              <div className="link-text">{window.location.origin}/friend/{roomCode}</div>
              <button className="btn-gold" onClick={copyLink} style={{ marginBottom: 10 }}>
                {copied ? t('linkCopied') : (lang==='en'?'Copy Invite Link':'复制邀请链接')}
              </button>
              <button className="btn-blue" onClick={enterCreatedRoom}>
                {lang==='en'?'Enter Room & Wait for Friends':'进入房间，等待好友'}
              </button>
            </>
          )}
        </div>

        {/* 加入房间（输入码） */}
        <div className="fl-card">
          <div className="fl-label">{lang==='en'?'Join a Friend\'s Room':'加入好友的房间'}</div>
          <input
            className="code-input"
            placeholder={lang==='en'?'Enter 6-digit room code':'输入6位房间码'}
            maxLength={6}
            value={joinCode}
            onChange={e => setJoinCode(e.target.value.toUpperCase())}
            onKeyDown={e => e.key === 'Enter' && joinRoom()}
          />
          <div style={{ height: 12 }} />
          <button className="btn-blue" onClick={joinRoom}>{lang==='en'?'Enter Room':'进入房间'}</button>
        </div>

        {/* 规则说明 */}
        <div className="fl-card">
          <div className="fl-label">{lang==='en'?'How to Play':'玩法说明'}</div>
          {lang==='en' ? [
            <>No sign-up needed. Join with a link or room code.</>,
            <><strong>10,000 PTS</strong> per player. No room cards or COIN required.</>,
            <>60-second battle with <strong>1000x leverage</strong> on live $ANSEM price.</>,
            <>Results show local ranking only. No effect on real account points.</>,
            <>Host creates room, shares link, friends join, host clicks Start.</>,
          ] : [
            <>无需注册，有链接或房间码即可加入</>,
            <>每人默认 <strong>10,000 PTS</strong>，不消耗房卡或 COIN</>,
            <>60秒对战，<strong>1000倍杠杆</strong>做多/做空 $ANSEM 实时价格</>,
            <>对战结果仅显示本局排名，不影响任何账号真实积分</>,
            <>房主创建房间后分享链接，好友点击进入，房主点击开始游戏</>,
          ].map((text, i) => (
            <div key={i} className="rule-row">
              <div className="rule-num">{i + 1}</div>
              <div className="rule-text">{text}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
